import React, { createContext, useEffect, useRef, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import io from 'socket.io-client';

interface QueueData {
  id: string;
  name: string;
  description: string;
  currentLength: number;
  estimatedServiceTime: number;
  status: 'active' | 'inactive' | 'maintenance';
  lastUpdated: string;
  // `counter` is the multi-counter service point a customer was directed to
  // (e.g. "Counter 3" / "Billing"), or null/absent when not in use.
  currentlyServing?: { position: number; customer_name: string; counter?: string | null } | null;
  /** Every customer currently at a counter — one per counter in use. */
  nowServing?: Array<{ position: number; customer_name?: string; counter?: string | null }>;
  /** Server-computed, already divided by the service points in use. */
  estimatedWaitTime?: number;
  /** The few numbers called before the current one. */
  recentlyCalled?: Array<{ position: number; customer_name?: string; counter?: string | null }>;
  activeCounters?: number;
  /** Data-URL QR pointing at this queue's join link. */
  joinQr?: string | null;
}

interface QueueUpdateData {
  queueId: string;
  type: 'participant_joined' | 'participant_left' | 'queue_advanced' | 'queue_reset' | 'customer_called';
  position?: number;
  estimatedWaitTime?: number;
  timestamp: string;
  // customer_called payload extras (backend emits camelCase `customerName`).
  customerName?: string;
  customer_name?: string;
  counter?: string | null;
}

export interface SocketContextType {
  socket: ReturnType<typeof io> | null;
  isConnected: boolean;
  queues: QueueData[];
  joinLocation: (locationId: string) => void;
  leaveLocation: (locationId: string) => void;
  joinQueue: (queueId: string) => void;
  leaveQueue: (queueId: string) => void;
  fetchQueue: (queueId: string) => Promise<void>;
  setInitialQueues: (queues: Array<{ id: string; name: string; description?: string; currentLength?: number; estimatedServiceTime?: number; status?: string }>) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export { SocketContext };

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const socketRef = useRef<ReturnType<typeof io> | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [queues, setQueues] = useState<QueueData[]>([]);

  useEffect(() => {
    // Create socket connection. Accept VITE_WS_URL too — that's the var
    // .env.production / Cloudflare actually set — so prod doesn't silently fall
    // back to localhost and lose real-time queue updates.
    const socketUrl =
      import.meta.env.VITE_SOCKET_URL ||
      import.meta.env.VITE_WS_URL ||
      'http://localhost:3003';

    socketRef.current = io(socketUrl, {
      autoConnect: true,
      transports: ['websocket', 'polling'],
    });

    const socket = socketRef.current;

    // Connection event handlers
    socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      setIsConnected(true);
    });

    socket.on('disconnect', (reason: string) => {
      console.log('Disconnected from WebSocket server:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (error: Error) => {
      console.error('WebSocket connection error:', error);
      setIsConnected(false);
    });

    // Queue event handlers
    socket.on('queue_update', (data: QueueUpdateData) => {
      console.log('Queue update received:', data);
      const { queueId, type } = data;
      if (!queueId || !type) return;

      setQueues(prevQueues => prevQueues.map(queue => {
        if (queue.id !== queueId) return queue;

        // A customer was called to the front, optionally directed to a specific
        // multi-counter service point. Update the NOW SERVING banner (including
        // the counter); waiting-count is driven by the participant events above.
        if (type === 'customer_called') {
          return {
            ...queue,
            currentlyServing: data.position != null
              ? {
                  position: data.position,
                  customer_name: data.customerName ?? data.customer_name ?? '',
                  counter: data.counter ?? null,
                }
              : queue.currentlyServing ?? null,
            // The called customer has left the waiting set, so the count drops
            // now rather than waiting for the next poll. The poll still has the
            // final say — this is only to keep the screen honest in between.
            currentLength: Math.max(0, queue.currentLength - 1),
            lastUpdated: new Date().toISOString(),
          };
        }

        let newLength = queue.currentLength;
        if (type === 'participant_joined') {
          newLength = queue.currentLength + 1;
        } else if (type === 'participant_left' || type === 'queue_advanced') {
          newLength = Math.max(0, queue.currentLength - 1);
        } else if (type === 'queue_reset') {
          newLength = 0;
        }

        return { ...queue, currentLength: newLength, lastUpdated: new Date().toISOString() };
      }));
    });

    socket.on('location_queues', (data: { queues: QueueData[] }) => {
      console.log('Location queues received:', data);
      setQueues(data.queues.map(queue => ({
        ...queue,
        lastUpdated: new Date().toISOString()
      })));
    });

    socket.on('queue_status', (data: QueueData) => {
      console.log('Queue status received:', data);
      setQueues(prevQueues =>
        prevQueues.map(queue =>
          queue.id === data.id
            ? { ...data, lastUpdated: new Date().toISOString() }
            : queue
        )
      );
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, []);

  const joinLocation = useCallback((locationId: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('join_location', { locationId });
      console.log('Joined location:', locationId);
    }
  }, [isConnected]);

  const leaveLocation = useCallback((locationId: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('leave_location', { locationId });
    }
  }, [isConnected]);

  const joinQueue = useCallback((queueId: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('join_queue', { queueId });
      console.log('Joined queue:', queueId);
    }
  }, [isConnected]);

  const leaveQueue = useCallback((queueId: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('leave_queue', { queueId });
    }
  }, [isConnected]);

  const fetchQueue = useCallback(async (queueId: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/api/queues/${queueId}`);
      if (response.ok) {
        const result = await response.json();
        const queueData = result.data || result; // Handle both {data: ...} and direct queue object
        console.log('Fetched queue data:', queueData);
        setQueues([{
          id: queueData.id,
          name: queueData.name,
          description: queueData.description || '',
          currentLength: queueData.statistics?.currentWaitTime || 0,
          estimatedServiceTime: queueData.estimated_service_time || queueData.settings?.estimatedServiceTime || 15,
          status: queueData.status || 'active',
          lastUpdated: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Failed to fetch queue:', error);
    }
  }, []);

  const setInitialQueues = useCallback((apiQueues: Array<{
    id: string;
    name: string;
    description?: string;
    currentLength?: number;
    current_length?: number;
    estimatedServiceTime?: number;
    estimated_service_time?: number;
    status?: string;
    currentlyServing?: { position: number; customer_name: string; counter?: string | null; called_to_counter?: string | null } | null;
    called_to_counter?: string | null;
    estimatedWaitTime?: number;
    estimated_wait_time?: number;
    recentlyCalled?: Array<{ position: number; customer_name?: string; counter?: string | null }>;
    recently_called?: Array<{ position: number; customer_name?: string; counter?: string | null }>;
    activeCounters?: number;
    active_counters?: number;
    joinQr?: string | null;
    join_qr?: string | null;
    nowServing?: Array<{ position: number; customer_name?: string; counter?: string | null }>;
    now_serving?: Array<{ position: number; customer_name?: string; counter?: string | null }>;
  }>) => {
    setQueues(apiQueues.map(q => ({
      id: q.id,
      name: q.name,
      description: q.description || '',
      currentLength: q.currentLength ?? q.current_length ?? 0,
      estimatedServiceTime: q.estimatedServiceTime ?? q.estimated_service_time ?? 15,
      status: (q.status as 'active' | 'inactive' | 'maintenance') || 'active',
      lastUpdated: new Date().toISOString(),
      // Server-computed: it divides by the number of service points actually
      // working the queue, which the client cannot know.
      estimatedWaitTime: q.estimatedWaitTime ?? q.estimated_wait_time ?? 0,
      recentlyCalled: q.recentlyCalled ?? q.recently_called ?? [],
      activeCounters: q.activeCounters ?? q.active_counters ?? 1,
      joinQr: q.joinQr ?? q.join_qr ?? null,
      nowServing: q.nowServing ?? q.now_serving ?? [],
      // Thread the served-to counter, tolerating either the nested `counter`,
      // the API's `called_to_counter`, or a queue-level `called_to_counter`.
      currentlyServing: q.currentlyServing
        ? {
            position: q.currentlyServing.position,
            customer_name: q.currentlyServing.customer_name,
            counter:
              q.currentlyServing.counter ??
              q.currentlyServing.called_to_counter ??
              q.called_to_counter ??
              null,
          }
        : null,
    })));
  }, []);

  const value: SocketContextType = {
    socket: socketRef.current,
    isConnected,
    queues,
    joinLocation,
    leaveLocation,
    joinQueue,
    leaveQueue,
    fetchQueue,
    setInitialQueues,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};